import React, {Component} from 'react';
import classNames from 'classnames';
import {bool, node, number, oneOf, string} from 'prop-types';
import styles from './TooltipWrapper.scss';
import {
  always,
  defaultTo,
  equals,
  mergeAll,
  pipe,
  pick,
  isEmpty,
  isNil,
  keys,
  map,
  reject,
} from 'ramda';

/**
 * An object containing keys that relate to css-styles for positioning
 * @typedef {Object} PositioningStyles
 * @property {?String} left - number of pixels to render to the left
 * @property {?String} right - number of pixels to render to the right
 * @property {?String} top - number of pixels to render to the top
 * @property {?String} bottom - number of pixels to render to the bottom
 */

/**
 * @typedef {Object} ClientRect
 * @property {Number} x - distance from the origin on the x-axis
 * @property {Number} y - distance from the origin on the y-axis
 * @property {Number} top - distance from the y-axis (same as y)
 * @property {Number} left - distance from the x-axis (same as x)
 * @property {Number} width - horizontal size of the element
 * @property {Number} height - vertical size of the element
 * @property {Number} bottom - top + height
 * @property {Number} right - left + width
 */


/**
 * compileClassNames - helper to join classNames
 * @param {String} baseStyle
 * @returns {function({position: *}): String}
 */
const compileClassNames = (baseStyle) => ({position}) => classNames(baseStyle, styles[position]);

/**
 * getBoxProps - takes an element as a target and extracts the client rectangle
 * @param {Element} element - target item to act on
 * @type {ClientRect}
 */
const getBoxProps = pipe(
  el => el.getBoundingClientRect(),
  // these are relative to the top left of the view-port.
  pick(['left', 'right', 'top', 'bottom', 'x', 'y', 'width', 'height'])
);

/**
 * getWindowBox - absolute position of the window
 * @returns {ClientRect}
 */
const getWindowBox = () => {
  const width = window.innerWidth;
  const height = window.innerHeight;

  const x = window.scrollX;
  const y = window.scrollY;

  return ({
    width,
    height,
    x,
    y,
    top: 0,
    left: 0,
    right: width,
    bottom: height,
  });
};

/**
 * isOffScreen - determines if the provided client rectangle is rendered off screen.
 * @param {ClientRect} domRect
 * @param {ClientRect} windowDims
 * @returns {Boolean}
 */
const isOffScreen = (domRect, windowDims = getWindowBox()) => {
  return domRect.left < windowDims.left
    || domRect.top < windowDims.top
    || domRect.right > windowDims.right
    || domRect.bottom > windowDims.bottom;
};

/**
 * greaterThanHalf - helper to determine if the left parameter is greater than half of the right parameter
 * @param {Number} isGreaterThan
 * @param {Number} target
 * @returns {Boolean}
 */
const greaterThanHalf = (isGreaterThan, target) => (isGreaterThan * 2) > target;

/**
 * chooseOne - helper to make a choice between two values, takes the two values and then takes a predicate and any
 *    arguments to be applied.  Gives back the left if the predicate (with ?args) is true, the right otherwise.
 * @param {*} left
 * @param {*} right
 * @returns {function(function, ...[*]): *}
 */
const chooseOne = (left, right) => (pred, ...args) => pred(...args) ? left : right;

const directions = {
  left: {
    opposite: always('right'),
    alternate: chooseOne('bottom', 'top'),
    alternateTest: ({bottom}, {height}) => greaterThanHalf(bottom, height),
    isEqual: equals('left'),
    default: defaultTo('left'),
  },
  right: {
    opposite: always('left'),
    alternate: chooseOne('bottom', 'top'),
    alternateTest: ({bottom}, {height}) => greaterThanHalf(bottom, height),
    isEqual: equals('right'),
    default: defaultTo('right'),
  },
  top: {
    opposite: always('bottom'),
    alternate: chooseOne('right', 'left'),
    alternateTest: ({right}, {width}) => greaterThanHalf(right, width),
    isEqual: equals('top'),
    default: defaultTo('top'),
  },
  bottom: {
    opposite: always('top'),
    alternate: chooseOne('right', 'left'),
    alternateTest: ({right}, {width}) => greaterThanHalf(right, width),
    isEqual: equals('bottom'),
    default: defaultTo('bottom'),
  },
};

/**
 * generateRepositionStyles - generates re-positioning style object
 * @param {ClientRect} box - element client rect
 * @param {ClientRect} parentBox - element's parent client rect
 * @param {ClientRect} windowBox - viewport client rect
 * @returns {PositioningStyles}
 */
const generateRepositionStyles = (box, parentBox, windowBox) => Object.assign(
  box.left < windowBox.left
    ? {left: (windowBox.left - parentBox.left) + 'px'}
    : {},
  box.right > windowBox.right
    ? {right: (parentBox.right - windowBox.width) + 'px'}
    : {},
  box.top < windowBox.top
    ? {top: (windowBox.top - parentBox.top) + 'px'}
    : {},
  box.bottom > windowBox.bottom
    ? {bottom: (parentBox.bottom - windowBox.height) + 'px'}
    : {},
);

/**
 * getOppositeOrAlternate -  use the string provided to determine which data to use and a predicate to determine
 *  which alternate to use.
 * returns a function that takes a string that is the original position, which will determine to use alternate or
 *  opposite.
 * @param {String} position
 * @param {function(): Boolean} alternateTest
 * @returns {function(?String): String}
 */
const getOppositeOrAlternate = (position, alternateTest) => {
  const helper = directions[position];
  return op => isNil(op) || !equals(position, op)
    ? helper.opposite()
    : helper.alternate(alternateTest);
};

/**
 * @typedef {Object} StatePositioning
 * @property {String} position - values: (top, bottom, left or right)
 * @property {String} [originalPosition] - values: top, bottom, left or right
 * @property {PositioningStyles} [tooltipOffsets]
 */

/**
 *
 * @param {StatePositioning} state
 * @param {ClientRect} box - Client Rectangle for the target element
 * @param {ClientRect} parentOffsets - Client Rectangle for the parent element
 * @param {ClientRect} windowBox - Client Rectangle for the window
 * @returns {function(*=): {originalPosition: *, position: *}}
 */

const determinePositionAttributes = (state, box, parentOffsets, windowBox) => {
  const {position, originalPosition} = state;
  const positioningHelper = directions[position];

  /*
    Expected input:
      originalPosition: null|undefined|top|bottom|left|right
        When null/undefined, it signifies that this it the initial pass through determining position.
        : result should generally be the opposite.
        if originalPosition is set and it equals the position, then we have problems, we need to use the alternate
   */
  const positionWhenHasOverflow = getOppositeOrAlternate(
    position,
    always(positioningHelper.alternateTest(parentOffsets, windowBox))
  );

  const baseObject = {
    originalPosition: originalPosition || position,
    position,
  };

  return (overflowDirection) => ({
    ...baseObject,
    position: positioningHelper.isEqual(overflowDirection)
      ? positionWhenHasOverflow(originalPosition)
      : position,
  });
};

const applyMaxes = (parentBox, windowBox, position) => (_styles) => {
  if (position === 'top' || position === 'bottom') {
    return {
      ..._styles,
      maxWidth: (windowBox.right - windowBox.left) + 'px',
      maxHeight: position === 'top'
        ? (windowBox.top - parentBox.top) + 'px'
        : (windowBox.bottom - parentBox.bottom) + 'px',
    };
  }
  if (position === 'left' || position === 'right') {
    return {
      ..._styles,
      maxHeight: (windowBox.bottom - windowBox.top) + 'px',
      maxWidth: position === 'left'
        ? (windowBox.left - parentBox.left) + 'px'
        : (windowBox.right - parentBox.right) + 'px',
    };
  }
  return _styles;
};


/**
 * @public
 * @param {StatePositioning} state - required to have position prop
 * @param {ClientRect} box - element client rect
 * @param {ClientRect} parentBox - element's parent client rect
 * @param {ClientRect} windowBox - viewport client rect
 * @returns {StatePositioning}
 */

/*
  Note:
  Assumption is the element being used for the tooltip cannot ever overflow the opposite direction than the current
  position.  E.g. positing the tooltip to the left of the anchor element should prevent the content from overflowing
  the right hand portion of the view port, all overflows should be in the left, top or bottom directions.
 */
export const determineDirection = (state, box, parentBox, windowBox) => {
  const repositionStyles = generateRepositionStyles(box, parentBox, windowBox);
  const innerFnPartial = determinePositionAttributes(state, box, parentBox, windowBox);

  const {position} = state;
  const opposite = directions[position].opposite();

  const applyMax = applyMaxes(parentBox, windowBox, position);

  return pipe(
    keys, // all the directions we have overflows
    reject(equals(opposite)), // See note above
    map(innerFnPartial),
    mergeAll,
    (result) => Object.assign(
      {
        // when the styles is empty: result in null,
        tooltipOffsets: isEmpty(repositionStyles) || result.position !== position
          ? null
          : applyMax(repositionStyles),
      },
      result
    )
  )(repositionStyles);
};

export const findNewPositioning = (tooltipRef, tooltipWrapperRef, {originalPosition, position, tooltipOffsets}) => {
  if (tooltipRef && tooltipOffsets === null) {
    const windowBox = getBoxProps(tooltipWrapperRef.offsetParent || {getBoundingClientRect: getWindowBox});
    const box = getBoxProps(tooltipRef);
    if (isOffScreen(box, windowBox)) {
      const parent = tooltipRef.offsetParent;
      const parentOffsets = getBoxProps(parent);
      return determineDirection({originalPosition, position}, box, parentOffsets, windowBox);
    }
  }
  return null;
};


export class TooltipWrapper extends Component {
  static propTypes = {
    className: string,
    children: node.isRequired,
    disabled: bool,
    showDelay: number,
    hideDelay: number,
    position: oneOf(['top', 'bottom', 'left', 'right']),
    tooltip: node.isRequired,
    lockOnOpen: bool,
  };

  static defaultProps = {
    disabled: false,
    hideDelay: 0,
    lockOnOpen: false,
    position: 'top',
    showDelay: 0,
  };

  constructor(props) {
    super(props);

    this.tooltipWrapper = null;

    this.state = {
      showTooltip: false,
      showTimer: null,
      hideTimer: null,
      tooltipOffsets: null,
      position: props.position,
      originalPosition: null,
    };
  }

  mouseOverHandler = () => {
    if (this.state.hideTimer !== null) {
      clearTimeout(this.state.hideTimer);
      this.setState({hideTimer: null});
    }

    const showTimer = setTimeout(
      () => {
        this.setState({
          showTooltip: true,
          showTimer: null,
        });
},
      this.props.showDelay || 0
    );
    this.setState({showTimer});
  };

  mouseOutHandler = () => {
    if (this.props.lockOnOpen !== true) {
      if (this.state.showTimer !== null) {
        clearTimeout(this.state.showTimer);
        this.setState({showTimer: null});
      }
      const hideTimer = setTimeout(
        () => this.setState({
          showTooltip: false,
          hideTimer: null,
          tooltipOffsets: null,
        }),
        this.props.hideDelay || 0
      );
      this.setState({hideTimer});
    }
  };

  componentWillReceiveProps(nextProps) {
    const {position} = nextProps;
    if (position !== this.props.position) {
      this.setState({position, originalPosition: null});
    }
  }

  componentDidUpdate() {
    if (!this.state.tooltipOffsets) {
      const positioning = findNewPositioning(this.tooltipRef, this.tooltipWrapper, this.state);
      !isEmpty(positioning) && this.setState(positioning);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.state.showTimer);
    clearTimeout(this.state.hideTimer);
  }

  render() {
    const tooltipActive = this.state.showTooltip && !this.props.disabled;
    this.tooltipRef = null;
    return (
      <span
        ref={ref => this.tooltipWrapper = ref}
        className={classNames(styles.TooltipWrapper, this.props.className)}
      >
        {tooltipActive && (
          <span
            ref={ref => this.tooltipRef = ref}
            style={this.state.tooltipOffsets}
            className={compileClassNames(styles.tooltip)(this.state)}
          >{this.props.tooltip}</span>
        )}
        <span
          className={
            compileClassNames(
              classNames(styles.container, {[styles.active]: tooltipActive})
            )(this.state)
          }
          onMouseOver={this.mouseOverHandler}
          onMouseOut={this.mouseOutHandler}
        >
          {this.props.children}
        </span>
      </span>
    );
  }
}

export default TooltipWrapper;
